import { defineMessages, FormattedMessage } from '@faceup/localization'
import { Button } from '@faceup/ui-base'
import { languageEnumToBasename } from '@faceup/utils'
import { useAccessKey } from '../App/AccessKeyProvider'
import { getInitialLanguage } from '../App/initialLanguage'
import { PlainLayout } from './PlainLayout'
import { UnavailableNotice } from './UnavailableNotice'

const messages = defineMessages('followUp.components.unavailableLink', [
  { id: 'title', defaultMessage: 'This link is no longer available' },
  {
    id: 'description',
    defaultMessage:
      'We could not find your organization. The link or the access code may have changed. Ask your organization for the current one.',
  },
  { id: 'button', defaultMessage: 'Back to the home page' },
] as const)

type UnavailableLinkProps = {
  /** the short link that failed to resolve; the remembered institution is dropped when it matches */
  shortId: string | undefined
}

/**
 * In the app the remembered institution is what sends the user here on every cold
 * start, so leaving drops it - otherwise the home redirect bounces straight back
 * here and the search form, the only place to enter a new access code, stays out
 * of reach. Leaving is a full page load rather than a client-side navigation: it
 * re-initializes the session from the cleared storage, and it also works from the
 * router's error boundary, which renders above the routes context `useNavigate`
 * reads.
 */
export const UnavailableLink = ({ shortId }: UnavailableLinkProps) => {
  const { accessKey, clearAccessKey } = useAccessKey()

  return (
    <PlainLayout>
      <div className='m-auto'>
        <UnavailableNotice
          title={<FormattedMessage message={messages.title} />}
          description={<FormattedMessage message={messages.description} />}
          action={
            <Button
              type='primary'
              data-test='unavailable-link-continue'
              onClick={() => {
                if (accessKey?.shortLink === shortId) {
                  clearAccessKey()
                }
                window.location.href = `/${languageEnumToBasename(getInitialLanguage())}`
              }}
            >
              <FormattedMessage message={messages.button} />
            </Button>
          }
        />
      </div>
    </PlainLayout>
  )
}
